import {
  ADDRESS_BOOK_CONTACTS_PAGINATED_FETCH_STARTED,
  ADDRESS_BOOK_CONTACTS_PAGINATED_FETCH_SUCCEEDED,
  ADDRESS_BOOK_CONTACTS_PAGINATED_FETCH_FAILED,
  ADDRESS_BOOK_CONTACTS_PAGINATED_CHANGE_PAGE,
  ADDRESS_BOOK_CONTACTS_PAGINATED_CHANGE_ITEMS_PER_PAGE,
  ADDRESS_BOOK_CONTACTS_PAGINATED_CHANGE_ORDER_AND_ORDER_BY,
  ADDRESS_BOOK_CONTACTS_PAGINATED_CLEAR
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { getRequestOptions } from '#helper';

const MAX_FILTER_CONTACTS = 20;

let abortController = new AbortController();

export const fetchContacts = ({ refetchAddressbooks = false, debounceTime = 200 } = {}) =>
  async (dispatch, getState) => {
    try {
      const {
        page,
        itemsPerPage,
        order,
        orderBy,
        addressbooks,
        filterBy
      } = getState().addressbookContactsPaginated;
      const { addressbook: addressbookFilter } = getState().listFilter;
      abortController.abort();
      const limit = itemsPerPage;
      const offset = (page - 1) * itemsPerPage;
      const searchParams = new URLSearchParams({
        order,
        orderBy
      });
      const filter = addressbookFilter?.search?.value;

      if (filter) {
        searchParams.set('filterBy', filterBy);
        searchParams.set('filter', filter);
      } else {
        searchParams.set('pagination', 1);
        searchParams.set('limit', limit);
        searchParams.set('offset', offset);
      }

      const contactsUrl = webPortalApi.buildApiUrl(`addressbook/contact/details?${searchParams.toString()}`);
      const addressbooksUrl = webPortalApi.buildApiUrl('addressbook?includeContactsCount');

      dispatch({
        type: ADDRESS_BOOK_CONTACTS_PAGINATED_FETCH_STARTED
      });
      abortController = new AbortController();
      const { signal } = abortController;
      // Debounce to do not send multiple requests in short time
      await new Promise(res => {
        setTimeout(() => {
          res();
        }, debounceTime);
      });
      const requests = [
        fetch(contactsUrl, { ...getRequestOptions(), signal })
      ];
      if (!addressbooks || refetchAddressbooks) {
        requests.push(fetch(addressbooksUrl, { ...getRequestOptions(), signal }));
      }

      const responses = await Promise.all(requests);
      const contentTypes = responses.map(res => res.headers.get('content-type'));
      if (contentTypes.some(contentType => (!contentType || !contentType.includes('application/json')))) {
        throw new TypeError('Response is not json');
      }

      const notOkResponses = responses.filter(res => !res.ok);
      if (notOkResponses.length) {
        throw await Promise.all(notOkResponses.map(res => res.json()));
      }
      let contacts = [];
      let last;
      const [
        contactsFetched,
        addressbooksFetched
      ] = await Promise.all(responses.map(res => res.json()));

      if (filter) {
        contacts = contactsFetched ?? [];
      } else {
        contacts = contactsFetched.contacts ?? [];
        last = contactsFetched.last;
      }

      // Add projects to contacts' address book
      const contactsWithProjects = contacts.map(contact => {
        const result = { ...contact };
        const addressbook = (addressbooksFetched || addressbooks)
          ?.find(ab => ab.id === contact?.addressbook?.id);
        if (addressbook) {
          result.addressbook = addressbook;
        }
        return result;
      });

      dispatch({
        type: ADDRESS_BOOK_CONTACTS_PAGINATED_FETCH_SUCCEEDED,
        payload: filter ? contactsWithProjects.slice(0, MAX_FILTER_CONTACTS) : contactsWithProjects,
        totalCount: last,
        addressbooks: addressbooksFetched
      });
    } catch (e) {
      if (e.name === 'AbortError') {
        return;
      }
      dispatch({
        type: ADDRESS_BOOK_CONTACTS_PAGINATED_FETCH_FAILED,
        error: e
      });
    }
  };

export const changePage = newPage => dispatch => {
  dispatch({
    type: ADDRESS_BOOK_CONTACTS_PAGINATED_CHANGE_PAGE,
    payload: newPage
  });
  dispatch(fetchContacts());
};

export const changeItemsPerPage = newItemsPerPage => dispatch => {
  dispatch({
    type: ADDRESS_BOOK_CONTACTS_PAGINATED_CHANGE_ITEMS_PER_PAGE,
    payload: newItemsPerPage
  });
  dispatch(fetchContacts());
};

export const changeOrderAndOrderBy = (newOrder, newOrderBy) => dispatch => {
  dispatch({
    type: ADDRESS_BOOK_CONTACTS_PAGINATED_CHANGE_ORDER_AND_ORDER_BY,
    payload: { order: newOrder, orderBy: newOrderBy }
  });
  dispatch(fetchContacts());
};

export const clear = () => dispatch => {
  abortController.abort();
  dispatch({
    type: ADDRESS_BOOK_CONTACTS_PAGINATED_CLEAR
  });
};
